<template>
  <div class="tickets-list-container">
    <!-- Filters -->
    <b-card class="filter-card shadow-sm">
      <div class="d-flex align-items-center mb-2">
        <feather-icon
          icon="FilterIcon"
          size="20"
          class="text-primary mr-1"
        />
        <h5 class="mb-0">
          Filtres
        </h5>
      </div>
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="Statut"
            label-for="status-filter"
            class="mb-md-0"
          >
            <v-select
              id="status-filter"
              v-model="filters.status"
              :options="statusOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Tous les statuts"
              :clearable="true"
              @input="onFilterChange"
            >
              <template #option="{ label, icon, variant }">
                <div class="d-flex align-items-center">
                  <feather-icon
                    :icon="icon"
                    size="16"
                    :class="`text-${variant} mr-50`"
                  />
                  <span>{{ label }}</span>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="Type"
            label-for="type-filter"
            class="mb-md-0"
          >
            <v-select
              id="type-filter"
              v-model="filters.ticket_type"
              :options="typeOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Tous les types"
              :clearable="true"
              @input="onFilterChange"
            >
              <template #option="{ label, icon, variant }">
                <div class="d-flex align-items-center">
                  <feather-icon
                    :icon="icon"
                    size="16"
                    :class="`text-${variant} mr-50`"
                  />
                  <span>{{ label }}</span>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="Recherche"
            label-for="search"
            class="mb-0"
          >
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  size="16"
                />
              </b-input-group-prepend>
              <b-form-input
                id="search"
                v-model="filters.search"
                placeholder="Rechercher par titre, ID..."
                @input="debouncedSearch"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Active Filters Display -->
      <div
        v-if="hasActiveFilters"
        class="active-filters mt-2 pt-2 border-top"
      >
        <div class="d-flex align-items-center flex-wrap">
          <span class="text-muted mr-1"><small>Filtres actifs:</small></span>
          <b-badge
            v-if="filters.status"
            variant="light-secondary"
            pill
            class="mr-50 mb-50"
          >
            Statut: {{ getStatusLabel(filters.status) }}
            <feather-icon
              icon="XIcon"
              size="14"
              class="cursor-pointer ml-25"
              @click="filters.status = null; onFilterChange()"
            />
          </b-badge>
          <b-badge
            v-if="filters.ticket_type"
            variant="light-secondary"
            pill
            class="mr-50 mb-50"
          >
            Type: {{ getTypeLabel(filters.ticket_type) }}
            <feather-icon
              icon="XIcon"
              size="14"
              class="cursor-pointer ml-25"
              @click="filters.ticket_type = null; onFilterChange()"
            />
          </b-badge>
          <b-badge
            v-if="filters.search"
            variant="light-secondary"
            pill
            class="mr-50 mb-50"
          >
            Recherche: "{{ filters.search }}"
            <feather-icon
              icon="XIcon"
              size="14"
              class="cursor-pointer ml-25"
              @click="filters.search = ''; onFilterChange()"
            />
          </b-badge>
          <b-button
            v-if="hasActiveFilters"
            variant="flat-secondary"
            size="sm"
            class="mb-50"
            @click="clearAllFilters"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
            Effacer tout
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- Tickets List -->
    <b-card
      v-if="loading"
      class="text-center py-5 loading-card"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-2 mb-0 text-primary font-weight-bold">
        Chargement des tickets...
      </p>
    </b-card>

    <b-card
      v-else-if="tickets.length === 0"
      class="text-center py-5 empty-state-card"
    >
      <div class="empty-state-icon-wrapper">
        <feather-icon
          icon="InboxIcon"
          size="64"
          class="text-muted"
        />
      </div>
      <h4 class="mt-2 mb-1">
        Aucun ticket trouvé
      </h4>
      <p class="text-muted">
        {{ hasActiveFilters ? 'Aucun ticket ne correspond à vos critères de recherche.' : 'Vous n\'avez pas encore créé de tickets.' }}
      </p>
      <b-button
        v-if="hasActiveFilters"
        variant="outline-primary"
        @click="clearAllFilters"
      >
        <feather-icon
          icon="RefreshCwIcon"
          class="mr-50"
        />
        Réinitialiser les filtres
      </b-button>
    </b-card>

    <div
      v-else
      class="tickets-grid"
    >
      <transition-group
        name="ticket-list"
        tag="div"
      >
        <b-card
          v-for="ticket in tickets"
          :key="ticket.id"
          class="ticket-card shadow-sm mb-2"
          @click="viewTicket(ticket)"
        >
          <div class="ticket-header d-flex align-items-start mb-2">
            <b-avatar
              :variant="`light-${getTypeVariant(ticket.ticket_type)}`"
              rounded
              size="56"
              class="mr-1 ticket-avatar"
            >
              <feather-icon
                :icon="getTypeIcon(ticket.ticket_type)"
                size="28"
              />
            </b-avatar>
            <div class="flex-grow-1">
              <div class="d-flex align-items-center justify-content-between mb-50">
                <h5 class="mb-0 ticket-title">
                  {{ ticket.title }}
                </h5>
                <b-badge
                  :variant="getStatusVariant(ticket.status)"
                  pill
                  class="status-badge"
                >
                  <feather-icon
                    :icon="getStatusIcon(ticket.status)"
                    size="12"
                    class="mr-25"
                  />
                  {{ ticket.status_display }}
                </b-badge>
              </div>
              <div class="ticket-meta">
                <b-badge
                  variant="light-secondary"
                  class="meta-badge"
                >
                  <feather-icon
                    icon="HashIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ ticket.ticket_id }}
                </b-badge>
                <b-badge
                  :variant="`light-${getTypeVariant(ticket.ticket_type)}`"
                  class="meta-badge"
                >
                  <feather-icon
                    :icon="getTypeIcon(ticket.ticket_type)"
                    size="12"
                    class="mr-25"
                  />
                  {{ ticket.ticket_type_display }}
                </b-badge>
                <b-badge
                  :variant="`light-${getPriorityVariant(ticket.priority)}`"
                  class="meta-badge"
                >
                  <feather-icon
                    :icon="getPriorityIcon(ticket.priority)"
                    size="12"
                    class="mr-25"
                  />
                  {{ ticket.priority_display }}
                </b-badge>
              </div>
            </div>
          </div>

          <div class="ticket-description text-muted mb-2">
            <p class="mb-0 ticket-desc-text">
              {{ truncateDescription(ticket.description) }}
            </p>
          </div>

          <div class="ticket-footer d-flex align-items-center justify-content-between">
            <div class="ticket-dates">
              <small class="text-muted d-block">
                <feather-icon
                  icon="ClockIcon"
                  size="12"
                  class="mr-25"
                />
                Créé {{ formatDate(ticket.created_at) }}
              </small>
              <small
                v-if="ticket.updated_at !== ticket.created_at"
                class="text-muted d-block"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="12"
                  class="mr-25"
                />
                Mis à jour {{ formatDate(ticket.updated_at) }}
              </small>
            </div>
            <div class="ticket-actions">
              <b-badge
                v-if="ticket.comments_count > 0"
                variant="light-info"
                pill
                class="mr-50"
              >
                <feather-icon
                  icon="MessageCircleIcon"
                  size="12"
                  class="mr-25"
                />
                {{ ticket.comments_count }}
              </b-badge>
              <feather-icon
                icon="ChevronRightIcon"
                size="20"
                class="text-muted"
              />
            </div>
          </div>
        </b-card>
      </transition-group>

      <!-- Pagination -->
      <b-pagination
        v-if="totalPages > 1"
        v-model="currentPage"
        :total-rows="totalItems"
        :per-page="perPage"
        align="center"
        @change="onPageChange"
      />
    </div>

    <!-- Ticket Detail Modal -->
    <ticket-detail-modal
      v-if="selectedTicket"
      v-model="showDetailModal"
      :ticket-id="selectedTicket.id"
      @ticket-updated="fetchTickets"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BSpinner,
  BBadge, BAvatar, BPagination, BButton, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@/libs/axios'
import { formatDate } from '@core/utils/filter'
import TicketDetailModal from './TicketDetailModal.vue'

export default {
  name: 'MyTicketsList',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BSpinner,
    BBadge,
    BAvatar,
    BPagination,
    BButton,
    BInputGroup,
    BInputGroupPrepend,
    vSelect,
    TicketDetailModal,
  },
  data() {
    return {
      tickets: [],
      loading: false,
      filters: {
        status: null,
        ticket_type: null,
        search: '',
      },
      statusOptions: [
        {
          label: 'En attente', value: 'pending', icon: 'ClockIcon', variant: 'warning',
        },
        {
          label: 'En cours', value: 'in_progress', icon: 'PlayCircleIcon', variant: 'info',
        },
        {
          label: 'Résolu', value: 'resolved', icon: 'CheckCircleIcon', variant: 'success',
        },
        {
          label: 'Fermé', value: 'closed', icon: 'XCircleIcon', variant: 'secondary',
        },
        {
          label: 'Rejeté', value: 'rejected', icon: 'AlertOctagonIcon', variant: 'danger',
        },
      ],
      typeOptions: [
        {
          label: 'Bug Report', value: 'bug', icon: 'AlertOctagonIcon', variant: 'danger',
        },
        {
          label: 'Feature Request', value: 'feature', icon: 'LightbulbIcon', variant: 'primary',
        },
        {
          label: 'Support Technique', value: 'technical', icon: 'MessageCircleIcon', variant: 'success',
        },
      ],
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      totalPages: 1,
      selectedTicket: null,
      showDetailModal: false,
      searchTimeout: null,
    }
  },
  computed: {
    hasActiveFilters() {
      return this.filters.status || this.filters.ticket_type || this.filters.search
    },
  },
  mounted() {
    this.fetchTickets()
  },
  methods: {
    async fetchTickets() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.perPage,
        }

        if (this.filters.status) {
          params.status = this.filters.status
        }
        if (this.filters.ticket_type) {
          params.ticket_type = this.filters.ticket_type
        }
        if (this.filters.search) {
          params.search = this.filters.search
        }

        const response = await axios.get('/api/support/supplier-tickets/my-tickets/', { params })
        this.tickets = response.data.results
        this.totalItems = response.data.count
        this.totalPages = Math.ceil(this.totalItems / this.perPage)
      } catch (error) {
        console.error('Error fetching tickets:', error)
      } finally {
        this.loading = false
      }
    },
    onFilterChange() {
      this.currentPage = 1
      this.fetchTickets()
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchTickets()
      }, 500)
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchTickets()
    },
    clearAllFilters() {
      this.filters.status = null
      this.filters.ticket_type = null
      this.filters.search = ''
      this.currentPage = 1
      this.fetchTickets()
    },
    viewTicket(ticket) {
      this.selectedTicket = ticket
      this.showDetailModal = true
    },
    formatDate(date) {
      return formatDate(date, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
      })
    },
    truncateDescription(desc) {
      if (!desc) return ''
      return desc.length > 150 ? `${desc.substring(0, 150)}...` : desc
    },
    getStatusLabel(value) {
      const option = this.statusOptions.find(opt => opt.value === value)
      return option ? option.label : value
    },
    getTypeLabel(value) {
      const option = this.typeOptions.find(opt => opt.value === value)
      return option ? option.label : value
    },
    getTypeIcon(type) {
      const icons = {
        bug: 'AlertOctagonIcon',
        feature: 'LightbulbIcon',
        technical: 'MessageCircleIcon',
      }
      return icons[type] || 'HelpCircleIcon'
    },
    getTypeVariant(type) {
      const variants = {
        bug: 'danger',
        feature: 'primary',
        technical: 'success',
      }
      return variants[type] || 'secondary'
    },
    getStatusIcon(status) {
      const icons = {
        pending: 'ClockIcon',
        in_progress: 'PlayCircleIcon',
        resolved: 'CheckCircleIcon',
        closed: 'XCircleIcon',
        rejected: 'AlertOctagonIcon',
      }
      return icons[status] || 'InfoIcon'
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        resolved: 'success',
        closed: 'secondary',
        rejected: 'danger',
      }
      return variants[status] || 'secondary'
    },
    getPriorityIcon(priority) {
      const icons = {
        low: 'CheckCircleIcon',
        medium: 'AlertCircleIcon',
        high: 'AlertTriangleIcon',
        critical: 'AlertOctagonIcon',
      }
      return icons[priority] || 'AlertCircleIcon'
    },
    getPriorityVariant(priority) {
      const variants = {
        low: 'success',
        medium: 'warning',
        high: 'danger',
        critical: 'danger',
      }
      return variants[priority] || 'secondary'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.tickets-list-container {
  .filter-card {
    border-radius: 8px;
    margin-bottom: 1.5rem;

    ::v-deep {
      .card-body {
        padding: 1.5rem;
      }

      label {
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      .vs__dropdown-toggle {
        border-radius: 8px;
      }

      .form-control {
        border-radius: 8px;
      }
    }

    .active-filters {
      .cursor-pointer {
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          opacity: 0.7;
        }
      }
    }
  }  .loading-card {
    border-radius: 12px;
    border: none;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .empty-state-card {
    border-radius: 8px;

    .empty-state-icon-wrapper {
      display: inline-block;
      padding: 20px;
      border-radius: 50%;
    }
  }

  .tickets-grid {
    .ticket-card {
      border-radius: 12px;
      border: none;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 4px;
        height: 100%;
        background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
        transform: scaleY(0);
        transition: transform 0.3s ease;
      }

      &:hover {
        transform: translateY(-4px) translateX(4px);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;

        &::before {
          transform: scaleY(1);
        }

        .ticket-title {
          color: #667eea;
        }
      }

      ::v-deep .card-body {
        padding: 1.5rem;
      }

      .ticket-avatar {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
      }

      &:hover .ticket-avatar {
        transform: scale(1.05);
      }

      .ticket-title {
        font-size: 1.1rem;
        font-weight: 600;
        transition: color 0.3s ease;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }

      .ticket-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        .meta-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.35rem 0.65rem;
          font-size: 0.75rem;
          border-radius: 6px;
        }
      }

      .ticket-description {
        padding: 0.75rem;
        background: #f8f9fa;
        border-radius: 8px;
        margin-top: 1rem;

        .ticket-desc-text {
          font-size: 0.875rem;
          line-height: 1.5;
        }
      }

      .ticket-footer {
        padding-top: 1rem;
        border-top: 1px solid #efefef;
        margin-top: 1rem;

        .ticket-dates {
          small {
            display: flex;
            align-items: center;
            margin-bottom: 0.25rem;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        .ticket-actions {
          display: flex;
          align-items: center;
        }
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.4rem 0.8rem;
        font-size: 0.8rem;
        font-weight: 600;
        border-radius: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }
  }

  // Animations
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  .ticket-list-enter-active,
  .ticket-list-leave-active {
    transition: all 0.4s ease;
  }

  .ticket-list-enter,
  .ticket-list-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }

  .ticket-list-move {
    transition: transform 0.4s ease;
  }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
  .tickets-list-container {
    .ticket-card {
      background: #283046;

      .ticket-description {
        background: #161d31;
      }

      .ticket-footer {
        border-top-color: #3b4253;
      }
    }
  }
}
</style>
